import React, { useState, useEffect, useRef } from "react";

const TabbedView = ({ tabs, sendRenderedTabIds, resetKey }) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [renderedTabs, setRenderedTabs] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const tabsContainerRef = useRef(null);
  const dragTimeoutRef = useRef(null);

  useEffect(() => {
    setRenderedTabs([]);
    setActiveTabIndex(0); // Reset to the first tab
    sendRenderedTabIds(null);
  }, [resetKey]);

  useEffect(() => {
    if (tabsContainerRef.current && tabs[activeTabIndex]) {
      const tabElement = document.getElementById(
        `tab-${tabs[activeTabIndex].id}`
      );
      if (tabElement) {
        const container = tabsContainerRef.current;
        const tabRect = tabElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        if (tabRect.left < containerRect.left) {
          container.scrollLeft -= containerRect.left - tabRect.left + 8;
        } else if (tabRect.right > containerRect.right) {
          container.scrollLeft += tabRect.right - containerRect.right + 8;
        }
      }
    }
  }, [activeTabIndex, tabs]);

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
      }
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
        dragTimeoutRef.current = null;
      }
    };

    document.addEventListener("mouseup", handleGlobalMouseUp);

    return () => {
      document.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  }, [isDragging]);

  const setActiveTab = (tab, idx) => {
    if (!isDragging) {
      setActiveTabIndex(idx);
      if (!renderedTabs.includes(tab.id)) {
        const newRenderedTabs = [...renderedTabs, tab.id];
        setRenderedTabs(newRenderedTabs);
        if (sendRenderedTabIds) {
          sendRenderedTabIds(tab.id);
        }
      }
    }
  };

  const handleMouseDown = (e) => {
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
    }

    setStartX(e.pageX - tabsContainerRef.current.offsetLeft);
    setScrollLeft(tabsContainerRef.current.scrollLeft);

    dragTimeoutRef.current = setTimeout(() => {
      setIsDragging(true);
    }, 150);
  };

  const handleMouseUp = () => {
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
      dragTimeoutRef.current = null;
    }
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (
      dragTimeoutRef.current &&
      Math.abs(e.pageX - (startX + tabsContainerRef.current.offsetLeft)) > 5
    ) {
      clearTimeout(dragTimeoutRef.current);
      dragTimeoutRef.current = null;
      setIsDragging(true);
    }

    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - tabsContainerRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    tabsContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  return (
    <div className="flex flex-col h-full w-full mx-auto">
      <div
        className="flex-shrink-0 relative z-5 border-b border-gray-400/20"
        style={{ overflow: "hidden" }}
      >
        <div
          ref={tabsContainerRef}
          className="flex overflow-x-auto overflow-y-hidden dark-slim-scrollbar"
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          style={{
            cursor: isDragging ? "grabbing" : "grab",
            scrollBehavior: isDragging ? "auto" : "smooth",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <div className="flex space-x-1 whitespace-nowrap">
            {tabs.map((tab, index) => (
              <button
                key={tab.id}
                id={`tab-${tab.id}`}
                onClick={() => setActiveTab(tabs[index], index)}
                className={`
                  relative px-4 py-2 text-sm font-medium rounded-t-lg transition-colors focus:outline-none
                  ${
                    activeTabIndex === index
                      ? "bg-gray-800/30 backdrop-blur-xl text-white border-t border-x border-gray-400/20 z-5"
                      : "bg-gray-500/20 text-gray-300 hover:bg-gray-500/30"
                  }
                `}
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
                aria-current={activeTabIndex === index ? "page" : undefined}
                role="tab"
                aria-selected={activeTabIndex === index}
                aria-controls={`panel-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="tabbed-glass-card flex-grow overflow-y-auto overflow-x-hidden relative">
        {tabs.map((tab, index) => (
          <div
            key={tab.id}
            id={`panel-${tab.id}`}
            role="tabpanel"
            aria-labelledby={`tab-${tab.id}`}
            className={`h-full ${
              activeTabIndex === index ? "block" : "hidden"
            }`}
          >
            <div className="pt-6 h-full overflow-y-auto">{tab.content}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TabbedView;
